import React from 'react'
import { Link } from 'react-router-dom'
import Logo from '../Assets/JapanLogo.png'

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-2xl font-bold text-neutral-950">{title}</h2>
    <div className="space-y-3 text-neutral-700 leading-relaxed">{children}</div>
  </section>
)

export default function PrivacyPolicy() {
  return (
    <main className="min-h-screen bg-[#f7f2ec] text-neutral-900">
      <header className="border-b border-black/10 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={Logo} alt="Simanof Japan Tour" className="h-12 w-auto" />
            <span className="text-sm font-bold uppercase tracking-[0.18em] text-red-700">
              Simanof Japan Tour
            </span>
          </Link>

          <Link to="/" className="text-sm font-semibold text-neutral-700 hover:text-red-700">
            Back to site
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-4xl px-6 py-12">
        <div className="mb-10 space-y-4">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-red-700">
            Privacy Policy
          </p>
          <h1 className="text-4xl font-black tracking-tight text-neutral-950 md:text-5xl">
            Simanof Japan Tour App Privacy Policy
          </h1>
          <p className="text-neutral-600">Last updated: July 6, 2026</p>
          <p className="max-w-3xl text-lg leading-relaxed text-neutral-700">
            This Privacy Policy explains how Simanof Japan Tour collects, uses, and protects
            information when you use the Simanof Japan Tour mobile app and related tour services.
          </p>
        </div>

        <div className="space-y-10 rounded-lg bg-white p-6 shadow-sm ring-1 ring-black/5 md:p-8">
          <Section title="Information We Collect">
            <p>We may collect information that you provide when creating or using your app account, including:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Name, email address, and phone number.</li>
              <li>Country, city of origin, role, leader group, and instrument.</li>
              <li>Account login information used through Firebase Authentication.</li>
              <li>Messages and support conversations sent inside the app.</li>
              <li>Notification tokens and device identifiers used to deliver app notifications.</li>
              <li>Reports, blocking, or safety moderation information submitted through the app.</li>
              <li>Technical information processed by Firebase services, such as IP address, user agent, authentication logs, and security-related identifiers.</li>
            </ul>
          </Section>

          <Section title="How We Use Information">
            <p>We use this information to:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Create and manage tour participant accounts.</li>
              <li>Provide tour itinerary, contact, music, support, and messaging features.</li>
              <li>Send app notifications for chat messages, group messages, tour announcements, and important tour updates.</li>
              <li>Help the Simanof team respond to emergencies, logistics questions, and support requests.</li>
              <li>Send password reset emails and manage account access.</li>
              <li>Maintain safety, prevent abuse, review reports, and support moderation.</li>
              <li>Improve reliability, security, and performance of the app and related services.</li>
            </ul>
          </Section>

          <Section title="Firebase and Service Providers">
            <p>
              The app uses Firebase services, including Firebase Authentication and Cloud Firestore,
              to provide account, database, and messaging functionality. Firebase may process
              technical and account-related information to provide authentication, security, abuse
              prevention, database, and app functionality services.
            </p>
            <p>
              Firebase is provided by Google. You can learn more about Firebase privacy and security
              at{' '}
              <a
                href="https://firebase.google.com/support/privacy"
                className="font-semibold text-red-700 underline"
                target="_blank"
                rel="noreferrer"
              >
                firebase.google.com/support/privacy
              </a>
              .
            </p>
          </Section>

          <Section title="Notifications">
            <p>
              If you allow notifications, the app may collect and store a notification token linked
              to your account so we can send alerts for chat messages, group messages, tour
              announcements, and important tour updates. You can manage or disable notifications in
              your device settings.
            </p>
          </Section>

          <Section title="Sharing of Information">
            <p>
              We do not sell personal information. We may share information with trusted service
              providers that help operate the app, including Firebase/Google, or when required to
              comply with law, protect users, prevent abuse, or respond to safety concerns.
            </p>
          </Section>

          <Section title="Data Retention and Deletion">
            <p>
              We keep information for as long as needed to provide the tour app, support tour
              operations, maintain security, and meet legal or operational needs. You may delete your
              app account from the Settings screen in the app. Account deletion removes your app
              profile and requests deletion of your Firebase Authentication account.
            </p>
            <p>
              You may also contact us to request help accessing, correcting, or deleting your
              information.
            </p>
          </Section>

          <Section title="Children and Participants">
            <p>
              The app is intended for Simanof Japan Tour participants, families, and tour staff. If a
              parent or guardian believes a participant's information should be updated or removed,
              please contact us.
            </p>
          </Section>

          <Section title="Security">
            <p>
              We use reasonable administrative, technical, and organizational measures to protect
              information. No online service can guarantee complete security, but we work to protect
              accounts, messages, and tour data from unauthorized access.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              For privacy questions, account deletion help, or support requests, contact us at{' '}
              <a href="mailto:information@simanof.org" className="font-semibold text-red-700 underline">
                information@simanof.org
              </a>
              .
            </p>
          </Section>
        </div>
      </article>
    </main>
  )
}
